import { Injectable } from '@angular/core';
import {catchError, finalize, map, Observable, of, shareReplay, timeout} from "rxjs";
import { HttpClient, HttpResponse } from '@angular/common/http';
import { AuthenticationType, LoggingOrigin, LoggingStatus, UserAuthentication, UserToken } from '@core/models/domain/auth.domain';
import { PersistencyService } from './persistency.service';
import { ApiService } from './api.service';
import { DIGI_RESPONSE_KEYS, DigiResponse } from '@core/models/response/core';
import { UserAuthenticationResponse } from '@core/models/response/auth.response';
import {
  LoginRequest,
  CreateSchoolAccountRequest,
  ValidateCodeRequest,
  SetPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  VerifyInvitationEmailRequest,
  ValidateInvitationCodeRequest,
  SetInvitationPasswordRequest
} from '@core/models/request/auth.request';
import { extractHeaderValue, handleError } from '@core/utils/service';

/**
 * Failed-login reason the backend returns when the password was correct but the account must set a
 * new one, because its old password was stored in plaintext (DIGI-1743). Must match
 * SchoolAccountCommandService.PASSWORD_RESET_REQUIRED in lokt-services.
 */
export const PASSWORD_RESET_REQUIRED_REASON = 'Password reset required';

/** What the login page shows for {@link PASSWORD_RESET_REQUIRED_REASON}. */
export const PASSWORD_UPGRADED_MESSAGE =
  'For security we have upgraded how passwords are stored. Please use Forgot password to set a new one.';

/**
 * The backend's refusal for a school that has been deactivated (DIGI-1855): sent only once the
 * password was right. Must match SchoolAccountClosed.MESSAGE in lokt-services.
 */
export const SCHOOL_ACCOUNT_CLOSED_MESSAGE = 'This school account has been closed.';

/** Thrown by {@link AuthService.login} for {@link PASSWORD_RESET_REQUIRED_REASON}. */
export class PasswordResetRequiredError extends Error {
  constructor() {
    super(PASSWORD_RESET_REQUIRED_REASON);
    this.name = 'PasswordResetRequiredError';
  }
}

/**
 * AuthService
 *
 * This service provides methods for user authentication, account management, and authorization.
 * It facilitates communication with the backend for login, account creation, password management, and token handling.
 *
 * Key Features:
 * - Handles user login and account creation.
 * - Manages password reset and validation processes.
 * - Provides methods to check user login and authorization status.
 */

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  API_URI: string;
  isUserLoggedIn: boolean;
  isAdmin: boolean;

  /** Cap on how long the login request may stall before it errors out, so a lost
   *  or never-sent backend response surfaces as a retryable error rather than an
   *  indefinite spinner. */
  private readonly LOGIN_TIMEOUT_MS = 30_000;

  /** Cap on the token-refresh request. Without it, a stalled /refresh leaves the
   *  JwtInterceptor's isRefreshing flag stuck true for the app's lifetime, which
   *  wedges every later 401 until the browser is reloaded. The timeout guarantees
   *  the refresh always settles so that flag is always released. */
  private readonly REFRESH_TIMEOUT_MS = 15_000;

  // In-flight /refresh call shared by all subscribers. The backend rotates the refresh cookie
  // on every call, so two concurrent calls (or one cancelled mid-flight by an effect's
  // switchMap) can invalidate the cookie the other one just received.
  private pendingRefresh$: Observable<UserToken> | null = null;
  
  private readonly ENDPOINTS = {
    login: "/login",
    refresh: "/refresh",
    // Under /refresh on purpose: the HttpOnly refresh cookie is path-scoped to /school-account/refresh,
    // so the browser only sends it there. The backend needs it to revoke the session on logout.
    logout: "/refresh/logout",
    createSchoolAccount: "/create",
    resendValidationCode: "/resend-validation-code",
    validateCode: "/validate-code",
    requestPasswordReset: "/request-password-reset",
    setPassword: "/set-password",
    changePassword: "/change-password",
    invitation: "/invitation"
  };

  constructor(private http: HttpClient, private apiService: ApiService, private persistency: PersistencyService) {
    this.API_URI = this.apiService.getApiURL("AuthService", "school-account");
  }

  /**
   * Logs in a user by sending a login request to the server.
   *
   * This function extracts the `SchoolAccountId` from the response headers and maps the response body to a `UserAuthentication` object.
   * Updates the service's state to indicate the user is logged in and whether the user is an admin.
   *
   * @param {LoginRequest} request - The login request object containing user credentials.
   * @returns {Observable<UserAuthentication>} An observable that emits the `UserAuthentication` object or handles errors.
   */
  login(request: LoginRequest): Observable<UserAuthentication> {
    const url = this.API_URI + this.ENDPOINTS.login;
    console.log("AuthService: login URL", url);
    return this.http
      .put<DigiResponse<UserAuthenticationResponse>>(url, request, { observe: 'response' })
      .pipe(
          timeout(this.LOGIN_TIMEOUT_MS),
          map((response) => {
            const userAuth = this.handleLoginResponse(response.body!);
            if (userAuth) {
              this.isUserLoggedIn = true;
              this.isAdmin = userAuth.isAdmin;
            }
            return userAuth;
        }),
        catchError((error) => handleError<UserAuthentication>("Login request", error))
      );
  }

  /**
   * Exchanges the HttpOnly refresh token cookie for a new short-lived access token.
   * The refresh token is never read by JavaScript — the browser sends the cookie automatically.
   * withCredentials is required so the browser includes the cookie on cross-origin requests.
   *
   * @returns {Observable<UserToken>} An observable that emits the new access token plus the
   * account IDs carried on the refresh token, so the in-memory store can be restored without
   * relying on browser storage.
   */
  refreshToken(): Observable<UserToken> {
    if (!this.pendingRefresh$) {
      const url = this.API_URI + this.ENDPOINTS.refresh;
      this.pendingRefresh$ = this.http
        .get<DigiResponse<string>>(url, { withCredentials: true })
        .pipe(
          timeout(this.REFRESH_TIMEOUT_MS),
          map((response) => {
            if (response.statusCode.status === 'OK' && response.metaData?.[DIGI_RESPONSE_KEYS.TOKEN]) {
              return {
                token: response.metaData[DIGI_RESPONSE_KEYS.TOKEN] as string,
                userAccountId: (response.metaData[DIGI_RESPONSE_KEYS.USER_ACCOUNT_ID] as string) ?? '',
                schoolAccountId: (response.metaData[DIGI_RESPONSE_KEYS.SCHOOL_ACCOUNT_ID] as string) ?? '',
              };
            }
            throw new Error(response.statusCode.reason ?? 'Token refresh failed');
          }),
          catchError((error) => handleError<UserToken>('Refresh token', error)),
          finalize(() => this.pendingRefresh$ = null),
          // refCount: false keeps the HTTP call running to completion even if every
          // subscriber unsubscribes, so the cookie rotation is never aborted mid-flight.
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.pendingRefresh$;
  }

  /**
   * Invalidates the session server-side and clears the HttpOnly refresh token cookie.
   * Only the server can clear an HttpOnly cookie — this call is mandatory on logout.
   *
   * @returns {Observable<void>}
   */
  logoutSession(): Observable<void> {
    const url = this.API_URI + this.ENDPOINTS.logout;
    return this.http
      .post<void>(url, {}, { withCredentials: true })
      .pipe(
        map(() => void 0),
        catchError((error) => handleError<void>('Logout session', error))
      );
  }

  /**
   * Creates a new school account by sending a POST request to the server.
   *
   * This function maps the response to a `UserToken` object, which includes the token, user account ID, and school account ID.
   *
   * @param {CreateSchoolAccountRequest} request - The request object containing school account details.
   * @returns {Observable<UserToken>} An observable that emits the `UserToken` object or handles errors.            
   */
  createSchoolAccount(request: CreateSchoolAccountRequest): Observable<UserToken> {
    return this.http
      .post<DigiResponse<string>>(this.API_URI + this.ENDPOINTS.createSchoolAccount, request, { observe: 'response' })
      .pipe(
          map((response) => this.handleUserToken(response)),
          catchError((error) => handleError<UserToken>('Create school account', error))
      );
  }

  /**
   * Validates a code by sending a PUT request to the server.
   *
   * This function maps the response to a `UserToken` object, which includes the token, user account ID, and school account ID.
   *
   * @param {ValidateCodeRequest} request - The request object containing the validation code.
   * @returns {Observable<UserToken>} An observable that emits the `UserToken` object or handles errors.
   */
  validateCode(request: ValidateCodeRequest): Observable<UserToken> {
    return this.http
      .put<DigiResponse<string>>(this.API_URI + this.ENDPOINTS.validateCode, request, { observe: 'response' })
      .pipe(
          map((response) => this.handleUserToken(response)),
          catchError((error) => handleError<UserToken>("Validate code", error))
      );
  }

  /**
   * Resend the school creation validation code by sending a PUT request to the server.
   *
   * @returns {Observable<UserToken>} An observable that emits the `UserToken` object or handles errors.
   */
  resendValidationCode(): Observable<UserToken> {
    return this.http
      .put<DigiResponse<string>>(this.API_URI + this.ENDPOINTS.resendValidationCode, {}, { observe: 'response' })
      .pipe(
          map((response) => this.handleUserToken(response)),
          catchError((error) => handleError<UserToken>("Resend Validation code", error))
      );
  }

  /**
   * Initiates a password reset process by sending a PUT request to the server.
   *
   * This function maps the response to a `UserToken` object, which includes the token, user account ID, and school account ID.
   *
   * @param {ResetPasswordRequest} request - The request object containing password reset details.
   * @returns {Observable<UserToken>} An observable that emits the `UserToken` object or handles errors.
   */
  requestPasswordReset(request: ResetPasswordRequest): Observable<UserToken> {
    return this.http
      .put<DigiResponse<string>>(this.API_URI + this.ENDPOINTS.requestPasswordReset, request, { observe: 'response' })
      .pipe(
          map((response) => this.handleUserToken(response)),
          catchError((error) => handleError<UserToken>("Reset password request", error))
      );
  }

  /**
   * Sets a new password for the user by sending a PUT request to the server.
   *
   * This function extracts the user account identifier from the response headers and returns it if the operation is successful.
   *
   * @param {SetPasswordRequest} request - The request object containing the new password details.
   * @returns {Observable<string>} An observable that emits the user account identifier or handles errors.
   */
  setPassword(request: SetPasswordRequest): Observable<string> {
    return this.http
      .put<DigiResponse<string>>(this.API_URI + this.ENDPOINTS.setPassword, request, { observe: 'response' })
      .pipe(
          map((response) => {
            const loqtResponse: DigiResponse<string> = response.body!;
            if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
              return loqtResponse.body;
            }
            return undefined;
        }),
        catchError((error) => handleError<UserToken>("Set password", error))
      );
  }

  /**
   * Changes the authenticated user's password by verifying the current password
   * and setting a new one.
   *
   * @param {ChangePasswordRequest} request - The request containing currentPassword and newPassword.
   * @returns {Observable<string>} An observable that emits the user account identifier on success.
   */
  changePassword(request: ChangePasswordRequest): Observable<string> {
    return this.http
      .put<DigiResponse<string>>(this.API_URI + this.ENDPOINTS.changePassword, request, { observe: 'response' })
      .pipe(
          map((response) => {
            const loqtResponse: DigiResponse<string> = response.body!;
            if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
              return loqtResponse.body;
            }
            return undefined;
        }),
        catchError((error) => handleError<string>("Change password", error))
      );
  }

  /**
   * Processes the login response from the server.
   *
   * This function maps the response data to a `UserAuthentication` object, including user details, token, and logging histories.
   * Returns undefined if the response status is not OK.
   *
   * @param {DigiResponse<UserAuthenticationResponse>} response - The response object containing login details.
   * @returns {UserAuthentication | undefined} The `UserAuthentication` object or undefined if the response status is not OK.
   */
  private handleLoginResponse(response: DigiResponse<UserAuthenticationResponse>) : UserAuthentication {
    if (response.statusCode.status === "OK" && response.body) {
      return {
        id: response.body.id,
        userAccountId: response.metaData[DIGI_RESPONSE_KEYS.USER_ACCOUNT_ID],
        schoolAccountId: response.metaData[DIGI_RESPONSE_KEYS.SCHOOL_ACCOUNT_ID],
        token: response.metaData[DIGI_RESPONSE_KEYS.TOKEN],
        type: response.body.type as AuthenticationType,
        e164phoneNumber: response.body.e164phoneNumber,
        emailAddress: response.body.emailAddress,
        // The login response carries no role. Optimistic default; MainLayoutComponent replaces
        // AuthService.isAdmin with the real schoolUserType once the signed-in user is loaded.
        isAdmin: true,
        loggingHistories: (response.body.loggingHistories ?? []).map(history => ({
          id: history.id,
          userAccountId: history.accountId,
          status: history.status as LoggingStatus,
          failureReason: history.failureReason,
          origin: history.origin as LoggingOrigin,
          creationDatetime: new Date(history.creationDatetime),
          lastModifiedDatetime: new Date(history.lastModifiedDatetime),
        })),
        creationDatetime: new Date(response.body.creationDatetime),
        lastModifiedDatetime: new Date(response.body.lastModifiedDatetime),
      } as UserAuthentication;
    } else if (response.statusCode.reason === PASSWORD_RESET_REQUIRED_REASON) {
      throw new PasswordResetRequiredError();
    } else {
      const errorMessage = response.statusCode.reason ? response.statusCode.reason : "Login response status is not OK";
      throw new Error(errorMessage);
    }
  }

  /**
   * Processes the user token response from the server.
   *
   * This function maps the response data to a `UserToken` object, including the token, user account ID, and school account ID.
   * Returns undefined if the response status is not OK.
   *
   * @param {HttpResponse<DigiResponse<string>>} response - The response object containing user token details.
   * @returns {UserToken | undefined} The `UserToken` object or undefined if the response status is not OK.
   */
  private handleUserToken(response: HttpResponse<DigiResponse<string>>): UserToken {
    const loqtResponse: DigiResponse<string> = response.body!;
    if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
      const schoolAccountId = loqtResponse.metaData[DIGI_RESPONSE_KEYS.SCHOOL_ACCOUNT_ID];
      const token = loqtResponse.metaData[DIGI_RESPONSE_KEYS.TOKEN];
      const userAccountId = loqtResponse.metaData[DIGI_RESPONSE_KEYS.USER_ACCOUNT_ID];
      return {
        token: token,
        userAccountId,
        schoolAccountId
      } as UserToken;
    }
    const errorMessage = loqtResponse.statusCode.reason ? loqtResponse.statusCode.reason : "Something went wrong with authentication service";
    throw new Error(errorMessage);
  }

  resolveInvitation(token: string): Observable<boolean> {
    const url = `${this.API_URI}${this.ENDPOINTS.invitation}/${token}`;
    return this.http
      .get<DigiResponse<null>>(url)
      .pipe(
        map((res) => res.statusCode.status === 'OK'),
        catchError((error) => handleError<boolean>('Resolve invitation', error))
      );
  }

  verifyInvitationEmail(request: VerifyInvitationEmailRequest): Observable<void> {
    const url = `${this.API_URI}${this.ENDPOINTS.invitation}/verify-email`;
    return this.http
      .post<DigiResponse<null>>(url, request)
      .pipe(
        map((res) => {
          if (res.statusCode.status !== 'OK') {
            throw new Error(res.statusCode.reason ?? 'Failed to verify email');
          }
        }),
        catchError((error) => handleError<void>('Verify invitation email', error))
      );
  }

  validateInvitationCode(request: ValidateInvitationCodeRequest): Observable<void> {
    const url = `${this.API_URI}${this.ENDPOINTS.invitation}/validate-code`;
    return this.http
      .post<DigiResponse<null>>(url, request)
      .pipe(
        map((res) => {
          if (res.statusCode.status !== 'OK') {
            throw new Error(res.statusCode.reason ?? 'Invalid code');
          }
        }),
        catchError((error) => handleError<void>('Validate invitation code', error))
      );
  }

  setInvitationPassword(request: SetInvitationPasswordRequest): Observable<void> {
    const url = `${this.API_URI}${this.ENDPOINTS.invitation}/set-password`;
    return this.http
      .post<DigiResponse<null>>(url, request)
      .pipe(
        map((res) => {
          if (res.statusCode.status !== 'OK') {
            throw new Error(res.statusCode.reason ?? 'Failed to set password');
          }
        }),
        catchError((error) => handleError<void>('Set invitation password', error))
      );
  }

  /**
   * Returns an observable indicating whether the user is currently logged in.
   * Checks the in-memory login flag. Token storage is no longer backed by
   * localStorage — use the NgRx store or the refresh flow to determine auth status.
   *
   * @returns {Observable<boolean>} An observable that emits the user's login status.
   */
  isLoggedIn(): Observable<boolean> {
    return of(this.isUserLoggedIn);
  }

  /**
   * Checks if the user is authorized to perform admin-level actions.
   *
   * This function returns true if the user is logged in and is an admin.
   *
   * @returns {boolean} True if the user is authorized, otherwise false.
   */
  isAuthorized(): boolean {
    return this.isUserLoggedIn && this.isAdmin;
  }
}
